/**
 * Placeholder dashboard.
 *
 * Phase 1 only proves the pipeline: login works, the session cookie is honoured,
 * the database is reachable, and the exercise registry is being read from the
 * server rather than a hardcoded list.
 *
 * Phase 7 replaces this with the real exception view (§7): who is flagged, who
 * is behind, who has plateaued — answerable in under twenty seconds. It will
 * also carry the dose-reduction restore control (DECISIONS.md 2026-08-20).
 */

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, type ClinicianSessionInfo } from '../api.js';
import styles from './ClinicianDashboard.module.css';

interface HealthResponse {
  ok: boolean;
  database: string;
}

interface ExercisesResponse {
  exercises: Array<{ id: string; name: string; category: string }>;
}

export function ClinicianDashboard() {
  const navigate = useNavigate();

  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api.get<ClinicianSessionInfo>('/auth/me'),
    retry: false,
  });

  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<HealthResponse>('/health'),
    enabled: session.isSuccess,
  });

  const registry = useQuery({
    queryKey: ['exercises'],
    queryFn: () => api.get<ExercisesResponse>('/exercises'),
    enabled: session.isSuccess,
  });

  useEffect(() => {
    if (session.isError && session.error instanceof ApiError && session.error.status === 401) {
      navigate('/clinician/login', { replace: true });
    }
  }, [session.isError, session.error, navigate]);

  if (session.isPending) return <p className={styles.loading}>Loading…</p>;
  if (session.isError) return null;

  const exerciseCount = registry.data?.exercises.length ?? 0;

  return (
    <div className={styles.page}>
      <main className={styles.main}>
        <h1 className={styles.heading}>Foundation deployed</h1>
        <p className={styles.sub}>
          This screen exists to prove the pipeline works end to end; the real
          dashboard — who is flagged, who is behind, who has plateaued — arrives
          in Phase 7.
        </p>

        <p className={styles.sub}>
          <Link to="/clinician/patients">Patients and prescriptions →</Link>
        </p>

        <section className={styles.panel}>
          <h2 className={styles.panelHeading}>Pipeline check</h2>
          <ul className={styles.list}>
            <li>
              Clinician authentication
              <span className={`${styles.status} ${styles.ok}`}>working</span>
            </li>
            <li>
              Database connection
              <span
                className={`${styles.status} ${
                  health.data?.database === 'up' ? styles.ok : styles.down
                }`}
              >
                {health.isPending ? 'checking…' : (health.data?.database ?? 'unreachable')}
              </span>
            </li>
            <li>
              Exercise registry — {exerciseCount} registered
              <span className={`${styles.status} ${styles.ok}`}>
                {registry.isPending ? 'checking…' : 'read from server'}
              </span>
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}
